/**
 * HostOnlyUrlInput.tsx — 只填 host 的 URL 输入：左侧固定 `https://`、右侧固定 path，运营只录中间的 host。
 *
 * 用于 path 由契约写死、只有 host 可变的地址（框架通道原生回跳 `https://<host>/payment/checkout/success`，
 * 设计稿 PayerMax A2）：把 path 做成不可编辑的后缀，运营就不会把 success / cancel 填反或带上 query。
 * 前后缀与输入框拼成一体（相邻圆角归零、边框共用），`invalid` 时输入框描 `--neg` 边与 `--neg-soft` 光圈。
 */

import type { CSSProperties } from 'react';

const affixStyle: CSSProperties = {
  height: 'var(--input-h)',
  display: 'inline-flex',
  alignItems: 'center',
  padding: '0 8px',
  border: '1px solid var(--border)',
  background: 'var(--bg-subtle)',
  color: 'var(--fg-muted)',
  fontSize: 12,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
};

export function HostOnlyUrlInput({
  id,
  host,
  path,
  onChange,
  invalid = false,
  disabled = false,
  describedBy,
  placeholder = 'pay.example.com',
  maxWidth = 470,
}: {
  id?: string;
  host: string;
  /** 固定 path（含前导 `/`），只展示不提交 */
  path: string;
  onChange: (host: string) => void;
  invalid?: boolean;
  disabled?: boolean;
  describedBy?: string;
  placeholder?: string;
  maxWidth?: number;
}) {
  return (
    <div className="hstack" style={{ gap: 0, maxWidth }}>
      <span
        className="mono"
        style={{
          ...affixStyle,
          borderRight: 0,
          borderRadius: 'var(--radius) 0 0 var(--radius)',
        }}
      >
        https://
      </span>
      <input
        id={id}
        className="input mono"
        value={host}
        disabled={disabled}
        placeholder={placeholder}
        spellCheck={false}
        autoComplete="off"
        aria-invalid={invalid || undefined}
        aria-describedby={describedBy}
        onChange={(e) => onChange(e.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          borderRadius: 0,
          ...(invalid
            ? { borderColor: 'var(--neg)', boxShadow: '0 0 0 3px var(--neg-soft)' }
            : null),
        }}
      />
      <span
        className="mono"
        style={{
          ...affixStyle,
          borderLeft: 0,
          borderRadius: '0 var(--radius) var(--radius) 0',
        }}
      >
        {path}
      </span>
    </div>
  );
}
